import { useEffect, useState } from "react";

/**
 * Sibling of `useErrorReturnReady` for the success path. Returns a boolean
 * that flips `true` `delay` ms after `isSuccess` becomes `true`, and resets
 * to `false` whenever `isSuccess` flips back to `false`.
 *
 * Drives the in-place success-recovery transition: each trade form's CTA
 * shows e.g. `"Staked 0.5 USDv"` immediately on success, then transitions
 * (fade-shrink-blur) to `"Stake More USDv"` / `"Return to Sell"` once this
 * hook flips true. Clicking the recovery CTA returns to the form view with
 * the input CLEARED (the default reset behavior; success doesn't preserve
 * input the way error does — the user just completed an action and likely
 * wants a fresh start).
 *
 * @param isSuccess  True while the form is in its success phase.
 * @param delay      Milliseconds to wait before flipping ready. Default 2000ms.
 */
export function useSuccessReturnReady(isSuccess: boolean, delay = 2000): boolean {
    const [ready, setReady] = useState(false);

    useEffect(() => {
        if (!isSuccess) {
            setReady(false);
            return;
        }
        const t = window.setTimeout(() => setReady(true), delay);
        return (): void => window.clearTimeout(t);
    }, [isSuccess, delay]);

    return ready;
}
